import { alpha, Box, styled, Tooltip } from '@mui/material';

import type { ReactNode } from 'react';

const PREFIX = 'TSMED-GlassToolbar';

export const glassToolbarClasses = {
  root: `${PREFIX}-root`,
  item: `${PREFIX}-item`,
};

const Root = styled(Box, {
  name: PREFIX,
  slot: 'Root',
})(({ theme: { palette } }) => ({
  [`&.${glassToolbarClasses.root}`]: {
    display: 'flex',
    height: 40,
    background: alpha(palette.common.black, 0.3),
    borderRadius: 8,
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
  },
}));

const ItemBox = styled(Box, {
  name: PREFIX,
  slot: 'ItemBox',
  shouldForwardProp: prop => prop !== 'active' && prop !== 'disabled',
})<{ active?: boolean; disabled?: boolean }>(
  ({ active, disabled, theme: { palette } }) => ({
    [`&.${glassToolbarClasses.item}`]: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 40,
      height: 40,
      padding: '0 8px',
      cursor: disabled ? 'default' : 'pointer',
      background: active
        ? alpha(palette.common.white, 0.1)
        : alpha(palette.common.white, 0.06),
      color: active ? palette.primary.main : palette.text.primary,
      opacity: disabled ? 0.5 : 1,
      pointerEvents: disabled ? 'none' : 'auto',
      transition: 'all 0.2s ease-in-out',

      '&:hover': {
        background: !disabled ? alpha(palette.common.white, 0.14) : undefined,
      },

      '& svg': {
        color: active ? palette.primary.main : 'inherit',
      },
    },
  }),
);

export interface GlassToolbarItem {
  id: string | number;
  content: ReactNode;
  active?: boolean;
  disabled?: boolean;
  tooltip?: string;
  onClick: () => void;
}

export interface GlassToolbarProps {
  items: GlassToolbarItem[];
  className?: string;
}

export const GlassToolbar = ({ items, className }: GlassToolbarProps) => (
  <Root className={`${glassToolbarClasses.root} ${className || ''}`}>
    {items.map((item, idx) => (
      <Tooltip key={item.id} title={item.tooltip || ''}>
        <ItemBox
          className={glassToolbarClasses.item}
          active={item.active}
          disabled={item.disabled}
          onClick={item.onClick}
          sx={{
            borderTopLeftRadius: idx === 0 ? 8 : 0,
            borderBottomLeftRadius: idx === 0 ? 8 : 0,
            borderTopRightRadius: idx === items.length - 1 ? 8 : 0,
            borderBottomRightRadius: idx === items.length - 1 ? 8 : 0,
          }}
        >
          {item.content}
        </ItemBox>
      </Tooltip>
    ))}
  </Root>
);
